import { useState, useEffect } from "react";
import { auth, db, storage } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable, deleteObject } from "firebase/storage";
import { UploadCloud, Trash2, UserCircle, Edit3, Save } from "lucide-react";
import Sidebar from "../components/Sidebar";

function MyProfile() {
  const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";

  const [userData, setUserData] = useState({
    firstName: "",
    email: "",
    contactNumber: "",
    profilePic: "",
  });

  const [image, setImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // useEffect(() => {
  //   fetchUserData();
  // }, []);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserData();
      }
    });
    return () => unsubscribe();
  }, []);
  

  const fetchUserData = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      setUserData({
        firstName: data.firstName || "",
        email: data.email || "",
        contactNumber: data.contactNumber || "",
        profilePic: data.profilePic || DEFAULT_AVATAR,
      });
    }
  };

  const handleInputChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (event) => {
    if (event.target.files[0]) {
      const file = event.target.files[0];
      setImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const uploadProfilePic = async () => {
    const user = auth.currentUser;
    if (!user || !image) return;

    setUploading(true);

    if (userData.profilePic && userData.profilePic !== DEFAULT_AVATAR) {
      const oldPicRef = ref(storage, userData.profilePic);
      try {
        await deleteObject(oldPicRef);
      } catch (error) {
        console.warn("No previous profile picture to delete or already removed.");
      }
    }

    const storageRef = ref(storage, `assets/${user.uid}/profile_pic/${image.name}`);
    const uploadTask = uploadBytesResumable(storageRef, image);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        console.log(`Upload Progress: ${(snapshot.bytesTransferred / snapshot.totalBytes) * 100}%`);
      },
      (error) => {
        console.error("Upload failed:", error);
        alert(`Upload failed: ${error.message}`);
        setUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(storageRef);
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { profilePic: downloadURL });
        setUserData((prev) => ({ ...prev, profilePic: downloadURL }));
        setPreviewImage(null);
        setUploading(false);
      }
    );
  };

  const removeProfilePic = async () => {
    const user = auth.currentUser;
    if (!user) return;

    if (userData.profilePic && userData.profilePic.startsWith("https://firebasestorage.googleapis.com")) {
      const picRef = ref(storage, userData.profilePic);
      try {
        await deleteObject(picRef);
      } catch (error) {
        console.warn("Profile picture was already deleted or does not exist.");
      }
    }

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, { profilePic: DEFAULT_AVATAR });

    setUserData((prev) => ({ ...prev, profilePic: DEFAULT_AVATAR }));
    setPreviewImage(null);
  };

  const saveUserData = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      firstName: userData.firstName,
      contactNumber: userData.contactNumber,
    });

    setIsEditing(false);
  };

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 p-6 bg-gray-100 min-h-screen">
        <div className="max-w-3xl mx-auto bg-white shadow-md rounded-lg p-6 mt-6">
          <h2 className="text-3xl font-semibold text-gray-800">My Profile</h2>
          <p className="text-gray-500">View and update your profile details</p>

          {/* Profile Image Section */}
          <div className="flex flex-col items-center mt-5">
            <div className="relative w-32 h-32">
              <img
                src={previewImage || userData.profilePic}
                alt="Profile"
                className="w-32 h-32 object-cover rounded-full border-2 border-gray-300"
              />
              <label className="absolute bottom-2 right-2 bg-gray-800 text-white p-2 rounded-full cursor-pointer">
                <UploadCloud size={18} />
                <input type="file" className="hidden" onChange={handleImageChange} />
              </label>
            </div>

            {previewImage && (
              <button
                onClick={uploadProfilePic}
                className="mt-3 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition disabled:opacity-50"
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Save New Profile Picture"}
              </button>
            )}

            {userData.profilePic !== DEFAULT_AVATAR && (
              <button
                onClick={removeProfilePic}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
              >
                <Trash2 size={18} className="inline-block mr-1" /> Remove Profile Picture
              </button>
            )}
          </div>

          {/* Profile Info */}
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-gray-700 font-medium">Name</label>
              <input
                type="text"
                name="firstName"
                value={userData.firstName}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`border border-gray-300 rounded-md w-full p-2 mt-1 ${
                  isEditing ? "bg-white" : "bg-gray-100"
                }`}
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium">Email</label>
              <input
                type="email"
                name="email"
                value={userData.email}
                disabled
                className="border border-gray-300 rounded-md w-full p-2 mt-1 bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium">Contact Number</label>
              <input
                type="tel"
                name="contactNumber"
                value={userData.contactNumber}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`border border-gray-300 rounded-md w-full p-2 mt-1 ${
                  isEditing ? "bg-white" : "bg-gray-100"
                }`}
              />
            </div>

            <button
              onClick={isEditing ? saveUserData : () => setIsEditing(true)}
              className={`mt-4 px-4 py-2 text-white rounded-md transition ${
                isEditing ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isEditing ? <Save size={18} className="inline-block mr-1" /> : <Edit3 size={18} className="inline-block mr-1" />}
              {isEditing ? "Save Changes" : "Edit Profile"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default MyProfile;
